/**
 * useProductSuppliers — ViewModel for /manage/product-suppliers.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination". Composes
 * usePaginatedList for reads; owns the add + delete mutations. List
 * call carries picker refs (active products + active suppliers) so
 * the form's two dropdowns render without a second round-trip.
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type {
  ProductRef,
  ProductSupplierLinkDto,
  SupplierRef
} from '../models/product-supplier.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

export type {
  ProductRef,
  ProductSupplierLinkDto,
  SupplierRef
} from '../models/product-supplier.model'

export interface CreateProductSupplierLinkInput {
  readonly product_id: string
  readonly supplier_id: string
}

export interface UseProductSuppliersReturn {
  readonly items: ProductSupplierLinkDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  /** Active products for the form's product dropdown. */
  readonly products: ProductRef[]
  /** Active suppliers for the form's supplier dropdown (all types). */
  readonly suppliers: SupplierRef[]
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly add: (
    input: CreateProductSupplierLinkInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListExtras {
  readonly products: ProductRef[]
  readonly suppliers: SupplierRef[]
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: ProductSupplierLinkDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
    readonly picker: { readonly products: ProductRef[]; readonly suppliers: SupplierRef[] }
  }
}

const EMPTY_EXTRAS: ListExtras = { products: [], suppliers: [] }

export function useProductSuppliers(): UseProductSuppliersReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<ProductSupplierLinkDto, ListExtras>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.MANAGEPRODUCTSUPPLIERS}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return {
          items: body.data.items,
          total: body.data.total,
          extras: {
            products: body.data.picker.products,
            suppliers: body.data.picker.suppliers
          }
        }
      },
      [authedFetch]
    )
  })

  const add = useCallback(
    async (input: CreateProductSupplierLinkInput) => {
      const r = await mutateJson<ProductSupplierLinkDto>(
        authedFetch,
        APIROUTES.MANAGEPRODUCTSUPPLIERS,
        'POST',
        input
      )
      if (!r.ok) return r
      if (paged.page === 1) paged.insertItem(r.data)
      else paged.reload()
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANAGEPRODUCTSUPPLIERITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.removeItem((it) => it.id === id)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const extras = paged.extras ?? EMPTY_EXTRAS
  return {
    items: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    products: extras.products,
    suppliers: extras.suppliers,
    setPage: paged.setPage,
    reload: paged.reload,
    add,
    remove
  }
}
